import * as React from "react";
import { observer } from "mobx-react-lite";
import { Button } from "antd";
import TaskStatusesVisualStore from "../stores/TaskStatusesVisualStore";
import StandardWorkflowNotice from "./StandardWorkflowNotice";
import InfoBanner from "../../common/components/InfoBanner";
import {
    createCustomModeColumns,
    createStandardModeColumns,
    renderStatusesTable,
} from "./taskStatusColumns";

type Props = {
    areActionsEnabled: boolean;
    isCustomMode: boolean;
    onCreateCustomTaskType: () => void;
    onOpenRemoveSeparateSubtaskStatusesModal: () => void;
    onOpenSeparateSubtaskStatusesModal: () => void;
    store: TaskStatusesVisualStore;
    onAddSubtaskStatus: () => void;
    onAddTaskStatus: () => void;
};

const sectionTitleStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 600,
    margin: 0,
};

const TaskStatusesTabContent: React.FC<Props> = ({
    areActionsEnabled,
    isCustomMode,
    onCreateCustomTaskType,
    onOpenRemoveSeparateSubtaskStatusesModal,
    onOpenSeparateSubtaskStatusesModal,
    store,
    onAddSubtaskStatus,
    onAddTaskStatus,
}) => {
    const mainStatuses = store.filteredTaskStatuses.filter(
        (status) => !status.isSubtaskStatus,
    );
    const subtaskStatuses = store.filteredTaskStatuses.filter(
        (status) => status.isSubtaskStatus,
    );
    const standardModeColumns = createStandardModeColumns(store.taskStatuses);
    const customModeColumns = createCustomModeColumns(
        store,
        store.taskStatuses,
    );

    if (!store.selectedTaskType) {
        return null;
    }

    return (
        <>
            {isCustomMode ? (
                <>
                    <div
                        style={{
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 10,
                        }}
                    >
                        <div>
                            <h3 style={sectionTitleStyle}>Task statuses</h3>
                        </div>
                        <Button
                            disabled={!areActionsEnabled}
                            onClick={onAddTaskStatus}
                            type="primary"
                        >
                            Add task status
                        </Button>
                    </div>
                    {renderStatusesTable(
                        customModeColumns,
                        mainStatuses,
                        store.taskStatusesLoading,
                    )}
                </>
            ) : (
                <>
                    <div style={{ marginBottom: 16 }}>
                        <StandardWorkflowNotice
                            isSystemDefault={!!store.taskType?.isSystemDefault}
                            actionLink={
                                store.taskType?.isSystemDefault
                                    ? {
                                          disabled: !areActionsEnabled,
                                          label: "Create custom task type",
                                          onClick: onCreateCustomTaskType,
                                      }
                                    : undefined
                            }
                        />
                    </div>
                    {renderStatusesTable(
                        standardModeColumns,
                        mainStatuses,
                        store.taskStatusesLoading,
                    )}
                </>
            )}
            {isCustomMode && store.taskType?.distinctSubtaskStatuses && (
                <>
                    <div
                        style={{
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 10,
                            marginTop: 16,
                        }}
                    >
                        <div>
                            <h3 style={sectionTitleStyle}>Subtask statuses</h3>
                        </div>
                        <Button
                            disabled={!areActionsEnabled}
                            onClick={onAddSubtaskStatus}
                            type="primary"
                        >
                            Add subtask status
                        </Button>
                    </div>
                    {renderStatusesTable(
                        customModeColumns,
                        subtaskStatuses,
                        store.taskStatusesLoading,
                    )}
                    <div style={{ marginTop: 16 }}>
                        <InfoBanner
                            actionLink={{
                                disabled: !areActionsEnabled,
                                label: "Remove separate subtask statuses",
                                onClick:
                                    onOpenRemoveSeparateSubtaskStatusesModal,
                            }}
                            className="task-statuses__subtask-info-banner"
                            description="Separate subtask statuses are enabled."
                        />
                    </div>
                </>
            )}
            {isCustomMode && !store.taskType?.distinctSubtaskStatuses && (
                <div style={{ marginTop: 16 }}>
                    <InfoBanner
                        actionLink={{
                            disabled: !areActionsEnabled,
                            label: "Use separate subtask statuses",
                            onClick: onOpenSeparateSubtaskStatusesModal,
                        }}
                        className="task-statuses__subtask-info-banner"
                        description="Subtasks use the same statuses as tasks."
                    />
                </div>
            )}
        </>
    );
};

export default observer(TaskStatusesTabContent);
